import { ALL_AUTO_INSTALL_STEP_TYPES, ALL_BUILD_STEP_TYPES, ALL_INSTALL_STEP_TYPES, ALL_PREMIUM_TYPES, ALL_STEP_NAMES, allNonWrapperTypes, allPremiumTypes, allQuizTypes, allWithNameTypes, allWrapperTypes, } from './constants';
/** Propagated exports. */
export { ALL_BUILD_STEP_TYPES, ALL_INSTALL_STEP_TYPES, ALL_NON_INSTALL_BUILD_STEP_TYPES, ALL_PREMIUM_TYPES, ALL_TEST_STEP_TYPES, ALL_STEP_NAMES, ALL_AUTO_INSTALL_STEP_TYPES, BUILD_STEP_GROUP_DATA, TEST_STEP_GROUP_DATA, allPremiumTypes, } from './constants';
/**
 * Check if the given type is a wrapper step type.
 *
 * @param type The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isWrapperType(type) {
    return allWrapperTypes.has(type);
}
/**
 * Check if the given step is a wrapper step.
 *
 * @param step The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isWrapper(step) {
    return allWrapperTypes.has(step.type);
}
/**
 * Check if the given type is a wrapper step type.
 *
 * @param type The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isNonWrapperType(type) {
    return allNonWrapperTypes.has(type);
}
/**
 * Check if the given step is a wrapper step.
 *
 * @param step The type to check if it is a wrapper step type.
 * @returns If the given step is a wrapper step type.
 */
export function isNonWrapper(step) {
    return allNonWrapperTypes.has(step.type);
}
/**
 * Check if the given step is a quiz question step.
 *
 * @param step The step to check.
 * @returns Whether the given step is a quiz question.
 */
export function isQuizQuestion(step) {
    return allQuizTypes.has(step.type);
}
/**
 * Check if the given step is a jupyter notebook step.
 *
 * @param step The step to check.
 * @returns Whether the given step is a jupyter notebook step.
 */
export function isJupyterNotebook(step) {
    return step.type === 'jupyter-notebook';
}
/**
 * Check if the given step has a name.
 *
 * @param step The type to check if it has a name.
 * @returns If the given step has a name.
 */
export function hasName(step) {
    return allWithNameTypes.has(step.type);
}
const buildStepTypeSet = new Set(ALL_BUILD_STEP_TYPES);
const installStepTypeSet = new Set(ALL_INSTALL_STEP_TYPES);
const autoInstallStepTypeSet = new Set(ALL_AUTO_INSTALL_STEP_TYPES);
/**
 * Check if the given type is a build step type.
 *
 * @param type The step type to check if it is a build step type.
 * @returns If the given type is a build step.
 */
export function isBuildStepType(type) {
    return buildStepTypeSet.has(type);
}
/**
 * Check if the given step is a build step.
 *
 * @param step The step to check if it is a build step.
 * @returns If the given type is a build step.
 */
export function isBuildStep(step) {
    return buildStepTypeSet.has(step.type);
}
/**
 * Check if the given type is an install step type.
 *
 * @param type The step type to check.
 * @returns If the given type is an install step.
 */
export function isInstallStepType(type) {
    return installStepTypeSet.has(type);
}
/**
 * Check if the given step is an install step.
 *
 * @param step The step to check.
 * @returns If the given step is an install step.
 */
export function isInstallStep(step) {
    return installStepTypeSet.has(step.type);
}
/**
 * Check if the given step is a auto install step.
 *
 * @param step The step to check if it is a auto install step.
 * @returns If the given type is a auto install step.
 */
export function isAutoInstallStep(step) {
    return autoInstallStepTypeSet.has(step.type);
}
/**
 * Get the category of a step.
 *
 * @param step The step to check.
 * @returns The category it belongs in.
 */
export function getStepCategory(step) {
    if (isAutoInstallStep(step)) {
        return 'auto-install';
    }
    else if (isBuildStep(step)) {
        return 'build';
    }
    else {
        return 'test';
    }
}
/**
 * Get the rubric rows connected to this configuration.
 *
 * @param config The configuration to get the rubric rows of.
 * @returns A set of all the connected rubric rows.
 */
export function getConnectedRubricRowIds(config) {
    const res = new Set();
    for (const step of config.steps) {
        if (step.type === 'connect-rubric' && step.rubricRowId.isJust()) {
            res.add(step.rubricRowId.extract());
        }
    }
    return res;
}
/**
 * Creates a sensible name for the provided step to show the user, containing the relevant
 * information they may need.
 *
 * @param step The step.
 * @returns The step header.
 */
export const getStepName = (step) => {
    if (hasName(step) && !/^\s*$/g.test(step.name)) {
        return step.name;
    }
    return ALL_STEP_NAMES[step.type];
};
/**
 * Check if the given step type is a premium step type.
 *
 * @param type The type to check.
 * @returns Whether the given type is a premium step type.
 */
export function isPremiumType(type) {
    return allPremiumTypes.has(type);
}
/** Mapping from each premium step type to the tier addons it requires. */
const PREMIUM_TYPE_TO_ADDONS = {
    'prompt-engineering-question': ['AI_ENABLED', 'QUIZ_ENABLED'],
    'jupyter-notebook': ['JUPYTER_ENABLED'],
    'run-in-notebook': ['JUPYTER_ENABLED'],
    quiz: ['QUIZ_ENABLED'],
    'coding-question': ['QUIZ_ENABLED'],
    mcq: ['QUIZ_ENABLED'],
    'select-all-question': ['QUIZ_ENABLED'],
};
/** Mapping from tier addon name to the corresponding property on CourseTierAddons. */
const ADDON_TO_PROPERTY = {
    AI_ENABLED: 'aiEnabled',
    JUPYTER_ENABLED: 'jupyterEnabled',
    QUIZ_ENABLED: 'quizEnabled',
};
/**
 * Get the set of premium step types that are allowed by the given addon flags.
 * A step type is allowed only when ALL of its required addons are enabled.
 *
 * @param addons The addon flags for the course.
 * @returns The set of premium step types the course has access to.
 */
export function getAllowedPremiumSteps(addons) {
    return new Set(ALL_PREMIUM_TYPES.filter(type => PREMIUM_TYPE_TO_ADDONS[type].every(addon => addons[ADDON_TO_PROPERTY[addon]])));
}
/**
 * Get the addon names required for a given premium step type.
 *
 * @param type The premium step type.
 * @returns The addons that must all be enabled for this step type.
 */
export function getRequiredAddonsForPremiumType(type) {
    return PREMIUM_TYPE_TO_ADDONS[type];
}
/**
 * Find premium steps that are not allowed by the given tier addons.
 *
 * @param steps The steps to check.
 * @param addons The addon flags for the course.
 * @returns A list of restricted steps with their required addons, deduplicated by type.
 */
export function findRestrictedPremiumSteps(steps, addons) {
    const allowed = getAllowedPremiumSteps(addons);
    const seen = new Set();
    const restrictions = [];
    for (const { type } of steps) {
        if (isPremiumType(type) && !allowed.has(type) && !seen.has(type)) {
            seen.add(type);
            restrictions.push({ type, addons: getRequiredAddonsForPremiumType(type) });
        }
    }
    return restrictions;
}
/**
 * Find non-install step types in the given setup steps.
 *
 * @param steps The setup (build) steps to check.
 * @returns The set of step types that are not install steps.
 */
export function findDisallowedSetupStepTypes(steps) {
    const disallowed = new Set();
    for (const step of steps) {
        if (!isInstallStepType(step.type)) {
            disallowed.add(step.type);
        }
    }
    return disallowed;
}
/**
 * Check if the given step is a premium step.
 *
 * @param step The step to check.
 * @returns Whether the given step is a premium step.
 */
export function isPremium(step) {
    return allPremiumTypes.has(step.type);
}
/**
 * Filters a step type counts record to remove non-premium step types.
 *
 * @param counts A record mapping step type strings to their counts.
 * @returns A map containing only the premium step type entries.
 */
export function filterPremiumStepCounts(counts) {
    const result = new Map();
    for (const type of ALL_PREMIUM_TYPES) {
        const found = counts.get(type);
        if (found != null) {
            result.set(type, found);
        }
    }
    return result;
}
/**
 * Count the occurrences of each step type across the build and test configurations.
 *
 * @param buildConfig The build configuration to count steps from.
 * @param testConfig The test configuration to count steps from.
 * @returns A record mapping step type strings to their counts.
 */
export function countStepTypes(buildConfig, testConfig) {
    const counts = new Map();
    for (const step of [...buildConfig.steps, ...testConfig.steps]) {
        counts.set(step.type, (counts.get(step.type) ?? 0) + 1);
    }
    return counts;
}
/** A chunk of bash code. */
export class BashChunk {
    value;
    /** @param value The value of the bash code. */
    constructor(value) {
        this.value = value;
    }
    /**
     * Create a piece of bash code that retrieves a variable.
     *
     * @param varname The name of the variable to retrieve.
     * @returns A piece of bash code that will load the variable.
     */
    static variable(varname) {
        return new BashChunk(`\${${varname}}`);
    }
    /**
     * Quote the given string `inp` as `shlex.quote` from python would quote it.
     *
     * @param value The inputted string that should be quoted.
     * @returns A quoted bash chunk the same as python 3.11 quotes it.
     * @see {@link https://github.com/python/cpython/blob/3.11/Lib/shlex.py#L323-L334}.
     */
    static escape(value) {
        if (value.length === 0) {
            return new BashChunk("''");
        }
        if (/[^\w@%+=:,./-]/.test(value)) {
            const inner = value.replaceAll("'", "'\"'\"'");
            return new BashChunk(`'${inner}'`);
        }
        return new BashChunk(value);
    }
    /**
     * Concat two chunks together.
     *
     * @param other The value to append to this value.
     * @returns A fresh new bash chunk.
     */
    concat(...other) {
        return new BashChunk([this.value, ...other.map(e => e.value)].join(''));
    }
}
